import { useCallback, useEffect, useRef, useState } from "react"
import { toast } from "sonner"
import type { TimelinePlayerPlaybackApi } from "@/components/timeline-player"
import type { SketchVideoCorrectionConfig } from "@/components/sketch/sketch-video-correction-stage"
import { VIDEO_CAPTURE_UNAVAILABLE } from "@/lib/timeline"
import { getCsrfToken } from "@/lib/workspace-api"

export type SketchVideoMarkup = {
  id: string
  file: File
  previewUrl: string
  timecodeMs: number
  /** 1-based index for mention id / POST order. */
  mentionIndex: number
}

type Args = {
  open: boolean
  projectId: number
  config: SketchVideoCorrectionConfig | null | undefined
  onOpenChange: (open: boolean) => void
  startFailedMessage: string
  /** Shown when frame grab needs a local blob proxy but none is available. */
  captureUnavailableMessage: string
  /** Flatten ink onto a freshly captured frame. */
  buildMarkupBlob: (baseImage: HTMLImageElement) => Promise<Blob>
  /** Playhead settled on a new frame — stash/restore per-frame ink in the host. */
  onPlayheadSettled: (ms: number) => void
  /** After Save markup — ensure current ink is keyed to that frame. */
  onMarkupSaved?: (timecodeMs: number) => void
}

const CAPTURE_MAX_ATTEMPTS = 5
const CAPTURE_RETRY_BASE_MS = 200

function waitMs(ms: number): Promise<void> {
  return new Promise((resolve) => {
    window.setTimeout(resolve, ms)
  })
}

/**
 * Markups list + correct_video POST + playing/overlay sizing for SketchDialog video mode.
 */
export function useSketchVideoCorrection({
  open,
  projectId,
  config,
  onOpenChange,
  startFailedMessage,
  captureUnavailableMessage,
  buildMarkupBlob,
  onPlayheadSettled,
  onMarkupSaved,
}: Args) {
  const overlayRef = useRef<HTMLDivElement>(null)
  const playbackApiRef = useRef<TimelinePlayerPlaybackApi | null>(null)
  const onPlayheadSettledRef = useRef(onPlayheadSettled)
  onPlayheadSettledRef.current = onPlayheadSettled
  const onMarkupSavedRef = useRef(onMarkupSaved)
  onMarkupSavedRef.current = onMarkupSaved
  const submittingRef = useRef(false)
  const exportingRef = useRef(false)

  const [playing, setPlaying] = useState(false)
  const [mediaReady, setMediaReady] = useState(false)
  const [stageSize, setStageSize] = useState({ width: 640, height: 360 })
  const [markups, setMarkups] = useState<SketchVideoMarkup[]>([])
  const [submitting, setSubmitting] = useState(false)
  const [exporting, setExporting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [correctionPrompt, setCorrectionPrompt] = useState("")

  const revokeMarkups = useCallback((items: SketchVideoMarkup[]) => {
    items.forEach((m) => URL.revokeObjectURL(m.previewUrl))
  }, [])

  useEffect(() => {
    if (!open || !config) {
      setPlaying(false)
      setMediaReady(false)
      submittingRef.current = false
      exportingRef.current = false
      setSubmitting(false)
      setExporting(false)
      setError(null)
      setCorrectionPrompt("")
      playbackApiRef.current = null
      setMarkups((prev) => {
        revokeMarkups(prev)
        return []
      })
    }
  }, [open, config, revokeMarkups])

  useEffect(() => {
    if (!open || !config || playing || !mediaReady) return
    const el = overlayRef.current
    if (!el) return
    const sync = () => {
      const rect = el.getBoundingClientRect()
      const w = Math.max(1, Math.round(rect.width))
      const h = Math.max(1, Math.round(rect.height))
      setStageSize((prev) => (prev.width === w && prev.height === h ? prev : { width: w, height: h }))
    }
    sync()
    const ro = new ResizeObserver(sync)
    ro.observe(el)
    return () => ro.disconnect()
  }, [open, config, playing, mediaReady])

  const onMediaReady = useCallback(() => {
    setMediaReady(true)
    setError(null)
  }, [])

  const handlePlayheadSettled = useCallback((ms: number) => {
    onPlayheadSettledRef.current(ms)
  }, [])

  const onCaptureError = useCallback((message: string) => {
    setError(message)
  }, [])

  const captureFrameAtPlayhead = useCallback(async (): Promise<{ image: HTMLImageElement; timecodeMs: number }> => {
    const api = playbackApiRef.current
    if (!api) throw new Error("Video frame unavailable")
    const timecodeMs = Math.round(api.controls.currentMs())
    let lastError: unknown = null
    for (let attempt = 0; attempt < CAPTURE_MAX_ATTEMPTS; attempt++) {
      try {
        const image = await api.captureCurrentFrame()
        return { image, timecodeMs: Math.round(api.controls.currentMs()) || timecodeMs }
      } catch (e) {
        lastError = e
        const message = e instanceof Error ? e.message : String(e)
        if (/video frame unavailable/i.test(message) && attempt < CAPTURE_MAX_ATTEMPTS - 1) {
          await waitMs(CAPTURE_RETRY_BASE_MS * (attempt + 1))
          continue
        }
        throw e instanceof Error ? e : new Error(String(e))
      }
    }
    throw lastError instanceof Error ? lastError : new Error("Video frame unavailable")
  }, [])

  const addMarkup = useCallback(async (): Promise<SketchVideoMarkup | null> => {
    if (exportingRef.current || submittingRef.current) return null
    if (!config || markups.length >= config.markupMax || playing || !mediaReady) {
      return null
    }
    exportingRef.current = true
    setExporting(true)
    setError(null)
    try {
      const { image, timecodeMs } = await captureFrameAtPlayhead()
      const blob = await buildMarkupBlob(image)
      const mentionIndex = markups.length + 1
      const file = new File([blob], `correction-markup-${mentionIndex}-${Math.round(timecodeMs)}.png`, {
        type: "image/png",
      })
      const previewUrl = URL.createObjectURL(file)
      const item: SketchVideoMarkup = {
        id: `${Date.now()}-${mentionIndex}`,
        file,
        previewUrl,
        timecodeMs,
        mentionIndex,
      }
      setMarkups((prev) => [...prev, item])
      // Keep ink on canvas for this frame; host also stashes under timecode.
      onMarkupSavedRef.current?.(timecodeMs)
      return item
    } catch (e) {
      const raw = e instanceof Error ? e.message : String(e)
      const message =
        raw === VIDEO_CAPTURE_UNAVAILABLE || /tainted canvases/i.test(raw) ? captureUnavailableMessage : raw
      setError(message)
      return null
    } finally {
      exportingRef.current = false
      setExporting(false)
    }
  }, [config, markups.length, playing, mediaReady, captureFrameAtPlayhead, buildMarkupBlob, captureUnavailableMessage])

  const removeMarkup = useCallback((id: string) => {
    setMarkups((prev) => {
      const next = prev.filter((m) => m.id !== id)
      const removed = prev.find((m) => m.id === id)
      if (removed) URL.revokeObjectURL(removed.previewUrl)
      // Keep mention ids contiguous after delete.
      return next.map((m, i) => ({ ...m, mentionIndex: i + 1 }))
    })
  }, [])

  const seekToMarkup = useCallback((ms: number) => {
    const api = playbackApiRef.current
    if (!api) return
    api.controls.pause()
    setPlaying(false)
    api.controls.seek(ms)
    onPlayheadSettledRef.current(ms)
  }, [])

  const submitCorrection = useCallback(() => {
    if (submittingRef.current) return
    if (!config) return
    const text = correctionPrompt.trim()
    if (!text) return
    submittingRef.current = true
    const markupPayload = markups.map((m) => ({ file: m.file, timecodeMs: m.timecodeMs }))
    const cfg = config
    const shotId = config.shotId
    setSubmitting(true)
    setError(null)
    // Закрываем сразу; File из markups живёт после revoke preview URL.
    cfg.onQueued()
    onOpenChange(false)

    void (async () => {
      try {
        const csrf = getCsrfToken()
        const fd = new FormData()
        fd.append("authenticity_token", csrf)
        fd.append("correction_prompt", text)
        fd.append("source_version_id", String(cfg.sourceVersionId))
        fd.append("trim_start_ms", String(Math.round(cfg.sourceInMs)))
        fd.append("trim_end_ms", String(Math.round(cfg.sourceOutMs)))
        fd.append("correction_track_id", cfg.correctionTrackId)
        fd.append("correction_clip_id", cfg.correctionClipId)
        fd.append("timeline_start_ms", String(Math.round(cfg.timelineStartMs)))
        markupPayload.forEach((m) => {
          fd.append("correction_markups[]", m.file, m.file.name)
          fd.append("correction_markup_timecode_ms[]", String(Math.round(m.timecodeMs)))
        })

        const res = await fetch(`/projects/${projectId}/workspace/shots/${shotId}/correct_video`, {
          method: "POST",
          headers: {
            Accept: "application/json",
            "X-CSRF-Token": csrf,
            "X-Requested-With": "XMLHttpRequest",
          },
          credentials: "same-origin",
          body: fd,
        })
        const data = (await res.json().catch(() => ({}))) as { ok?: boolean; error?: string }
        if (!res.ok || !data.ok) {
          throw new Error(data.error ?? startFailedMessage)
        }
      } catch (e) {
        cfg.onQueueFailed?.()
        toast.error(e instanceof Error ? e.message : String(e))
      }
    })()
  }, [config, correctionPrompt, markups, projectId, startFailedMessage, onOpenChange])

  return {
    overlayRef,
    playbackApiRef,
    playing,
    setPlaying,
    mediaReady,
    stageSize,
    markups,
    submitting,
    exporting,
    error,
    correctionPrompt,
    setCorrectionPrompt,
    onMediaReady,
    onPlayheadSettled: handlePlayheadSettled,
    onCaptureError,
    addMarkup,
    removeMarkup,
    seekToMarkup,
    submitCorrection,
    canAddMarkup: config ? markups.length < config.markupMax : false,
  }
}
